import React, { useState, useEffect } from "react";
import { auth, db } from "./firebase/firebaseConfig";
import {
  collection,
  getDocs,
  query,
  where,
  doc,
  updateDoc,
  getDoc,
} from "firebase/firestore";
import Header from "./components/Header";
import { useTranslation } from "react-i18next";
import { useBanner } from "./components/BannerContext";

function CoachDashboard({ user }) {
  const [assignedAthletes, setAssignedAthletes] = useState([]);
  const [selectedAthlete, setSelectedAthlete] = useState("");
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [coachId, setCoachId] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const { t } = useTranslation();
  const { showBanner } = useBanner();

  useEffect(() => {
    const fetchCoachAndAthletes = async () => {
      const user = auth.currentUser;
      if (!user) return;

      const coachUID = user.uid;
      setCoachId(coachUID);

      // Get all users from Firestore
      const userSnap = await getDocs(collection(db, "users"));
      const allUsers = userSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      setAllUsers(allUsers);

      // Filter athletes assigned to this coach
      const athletes = allUsers.filter(
        (u) => u.role === "athlete" && u.coachId === coachUID
      );
      setAssignedAthletes(athletes);
    };

    fetchCoachAndAthletes();
  }, []);


  useEffect(() => {
    const fetchSessions = async () => {
      if (!selectedAthlete) return;

      const q = query(
        collection(db, "sessions"),
        where("coachId", "==", coachId),
        where("athleteId", "==", selectedAthlete)
      );
      const querySnapshot = await getDocs(q);
      const sessionList = querySnapshot.docs
        .map((doc) => ({ id: doc.id, ...doc.data() }))
        .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      setSessions(sessionList);
    };

    fetchSessions();
  }, [selectedAthlete]);

  const getUserEmail = (id) => {
    const user = allUsers.find((u) => u.id === id);
    return user?.email || id;
  };

  return (
    <>
      <Header user={user} />
      <div className="min-h-screen bg-white px-4 py-8">
        <div className="max-w-5xl mx-auto bg-[#f1efe7] rounded-xl shadow-md p-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">{t("coach_dashboard")}</h2>

          {/* Athlete Filter */}
          <div className="mb-6">
          <label>{t("filter_by_athlete")}</label>
          
            <select
              value={selectedAthlete}
              onChange={(e) => {
                setSelectedAthlete(e.target.value);
                setSelectedSession(null);
              }}
              className="w-full px-4 py-2 border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-[#b22222]"
            >
              <option value="">{t("select_athlete")}</option>
              {assignedAthletes.map((athlete) => (
                <option key={athlete.id} value={athlete.id}>
                  {athlete.name || athlete.email}
                </option>
              ))}
            </select>
          </div>

          {/* Session List */}
          {sessions.length === 0 ? (
            <p className="text-gray-600">{t("no_sessions")}</p>
          ) : (
            <ul className="space-y-4">
              {sessions.map((session) => (
                <li
                  key={session.id}
                  onClick={() => setSelectedSession(session)}
                  className={`cursor-pointer p-4 rounded-md border ${selectedSession?.id === session.id
                      ? "border-[#b22222] bg-white"
                      : "border-gray-300 bg-white hover:border-[#b22222]"
                    }`}
                >
                  <div className="flex justify-between items-center">
                    <span className="font-semibold">
                      {new Date(session.timestamp).toLocaleDateString()}
                    </span>
                    {session.tags?.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {session.tags.map((tag, index) => (
                          <span
                            key={index}
                            className="text-xs bg-[#b22222] text-white px-2 py-1 rounded-full"
                          >
                            {t(`tag_${tag.toLowerCase()}`) || tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 truncate mt-1">
                    {session.notes?.slice(0, 100)}...
                  </p>

                  {/* Expanded View */}
                  {selectedSession?.id === session.id && (
                    <div className="mt-4 border-t pt-4 text-sm text-gray-800">
                      <p><strong>{t("athlete")}:</strong> {getUserEmail(session.athleteId)}</p>
                      <p><strong>{t("date")}:</strong> {new Date(session.timestamp).toLocaleString()}</p>
                      <p><strong>{t("notes")}:</strong> {session.notes}</p>
                      {session.nextSteps && (
                        <p><strong>{t("next_steps")}:</strong> {session.nextSteps}</p>
                      )}
                      <p><strong>{t("mbti")}:</strong> {getUserEmail(session.athleteId, "mbti")}</p>
                      <p><strong>{t("enneagram")}:</strong> {getUserEmail(session.athleteId, "enneagram")}</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                        {session.goalIdentification && (
                          <div>
                            <strong>{t("goal_identification")}:</strong>
                            <p>{session.goalIdentification}</p>
                          </div>
                        )}
                        {session.strongSelfActivation && (
                          <div>
                            <strong>{t("strong_self_activation")}:</strong>
                            <p>{session.strongSelfActivation}</p>
                          </div>
                        )}
                        {session.personalityTypeIdentification && (
                          <div>
                            <strong>{t("personality_type_identification")}:</strong>
                            <p>{session.personalityTypeIdentification}</p>
                          </div>
                        )}
                        {session.copingStrategyIdentification && (
                          <div>
                            <strong>{t("coping_strategy_identification")}:</strong>
                            <p>{session.copingStrategyIdentification}</p>
                          </div>
                        )}
                        {session.reprogrammingForSuccess && (
                          <div>
                            <strong>{t("reprogramming_for_success")}:</strong>
                            <p>{session.reprogrammingForSuccess}</p>
                          </div>
                        )}
                        {session.emotionalEmpowerment && (
                          <div>
                            <strong>{t("emotional_empowerment")}:</strong>
                            <p>{session.emotionalEmpowerment}</p>
                          </div>
                        )}
                      </div>

                      {/* Attached Files */}
                      {session.files?.length > 0 && (
                        <div className="mt-4">
                          <strong>{t("attached_files")}:</strong>
                          <ul className="list-disc list-inside">
                            {session.files.map((url, index) => (
                              <li key={index}>
                                <a href={url} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">
                                  File {index + 1}
                                </a>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Coach Comment */}
                      <div className="mt-6">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t("add_comment")}
                        </label>
                        <textarea
                          rows="3"
                          value={session.tempCoachComment || ""}
                          onChange={(e) => {
                            setSessions((prev) =>
                              prev.map((s) =>
                                s.id === session.id ? { ...s, tempCoachComment: e.target.value } : s
                              )
                            );
                          }}
                          className="w-full px-4 py-2 border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-[#b22222]"
                          placeholder={t("comment_placeholder")}
                        />
                        <button
                          onClick={async () => {
                            try {
                              await updateDoc(doc(db, "sessions", session.id), {
                                coachComment: session.tempCoachComment,
                              });
                              showBanner("✅ Comment sent to therapist.", "success");
                              setSessions((prev) =>
                                prev.map((s) =>
                                  s.id === session.id
                                    ? {
                                      ...s,
                                      coachComment: session.tempCoachComment,
                                      tempCoachComment: "",
                                    }
                                    : s
                                )
                              );
                            } catch (error) {
                              console.error("❌ Failed to send comment:", error.message);
                              showBanner("Error sending comment.", "error");
                            }
                          }}
                          className="mt-2 bg-[#b22222] text-white px-4 py-2 rounded-md hover:bg-red-800 transition"
                        >
                          {t("send_to_therapist")}
                        </button>
                      </div>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </>
  );
}

export default CoachDashboard;
